/**
 * useDrawer composable
 * Handles all navigation drawer logic and configuration.
 *
 * @overviewItems Computed drawer items for the overview section.
 * @aboutItems Computed drawer items for the about section.
 * @navigationActivePathRules Defines additional "active" path rules for route highlighting.
 * @openDrawer Opens the drawer and determines its mode (rail or expanded).
 * @closeDrawer Closes the drawer completely.
 * @toggleDrawer Toggles the drawer’s open/rail/closed states.
 * @isPathActive Checks if a given drawer item path is currently active.
 */

//
// Imports
//

// Vuetify
import { useDisplay } from "vuetify";

// App Config / Types
import { drawerConfig } from "../../config/drawer-items";
import type { DrawerItemType } from "../../types/components/common/navigation-drawer";

export const useDrawer = () => {
  //
  // Composables
  //

  // Route
  const route = useRoute();

  // Localization
  const { t } = useI18n();
  const localePath = useLocalePath();

  // Breakpoints
  const { mdAndDown, mobile } = useDisplay();

  //
  // State
  //
  const drawer = useSessionState<boolean>("ui.drawer", true);
  const drawerRailMode = useSessionState<boolean>("ui.drawerRailMode", false);

  //
  // Computed Properties
  //
  const operationalItems = computed<DrawerItemType[]>(() =>
    drawerConfig.operational.map((item) => ({
      title: t(
        `components.common.navigation-drawer.drawer_sections.operational_section.drawer_items.${item.key}.title`,
      ),
      tooltipText: t(
        `components.common.navigation-drawer.drawer_sections.operational_section.drawer_items.${item.key}.tooltip_text`,
      ),
      icon: item.icon,
      to: localePath(item.path),
    })),
  );

  const administrativeItems = computed<DrawerItemType[]>(() =>
    drawerConfig.administrative.map((item) => ({
      title: t(
        `components.common.navigation-drawer.drawer_sections.administrative_section.drawer_items.${item.key}.title`,
      ),
      tooltipText: t(
        `components.common.navigation-drawer.drawer_sections.administrative_section.drawer_items.${item.key}.tooltip_text`,
      ),
      icon: item.icon,
      to: localePath(item.path),
    })),
  );

  const aboutItems = computed<DrawerItemType[]>(() =>
    drawerConfig.about.map((item) => ({
      title: t(`components.common.navigation-drawer.drawer_sections.about_section.drawer_items.${item.key}.title`),
      tooltipText: t(
        `components.common.navigation-drawer.drawer_sections.about_section.drawer_items.${item.key}.tooltip_text`,
      ),
      icon: item.icon,
      to: localePath(item.path),
    })),
  );

  const navigationActivePathRules = computed(
    () =>
      ({
        // Locations drawer item: active for all location sub-routes (detail, instrument detail, test detail).
        [localePath("/app/locations")]: [localePath("/app/locations")],
        // Instruments drawer item: active only for /app/instruments and its sub-routes.
        [localePath("/app/instruments")]: [localePath("/app/instruments")],
      }) as Record<string, string[]>,
  );

  //
  // Group Expansion
  //

  /** Whether the current route is under any administrative (laboratory) item path. */
  const isLaboratoryRoute = computed(() => {
    const locationsPath = localePath("/app/locations");
    const instrumentsPath = localePath("/app/instruments");
    return (
      route.path === locationsPath ||
      route.path.startsWith(`${locationsPath}/`) ||
      route.path === instrumentsPath ||
      route.path.startsWith(`${instrumentsPath}/`)
    );
  });

  /**
   * Groups that are currently expanded in the navigation drawer.
   * Persists across client-side navigations via useState.
   * Initialized open when the user lands on a laboratory route.
   */
  const openedGroups = useState<string[]>("ui.drawer.openedGroups", () =>
    isLaboratoryRoute.value ? ["laboratory"] : [],
  );

  /** Auto-expand the Laboratory group when navigating into a lab route, but never auto-collapse. */
  watch(isLaboratoryRoute, (isLab) => {
    if (isLab && !openedGroups.value.includes("laboratory")) {
      openedGroups.value = [...openedGroups.value, "laboratory"];
    }
  });

  //
  // Methods
  //
  const openDrawer = () => {
    drawer.value = true;
    drawerRailMode.value = !mobile.value && !mdAndDown.value;
  };

  const closeDrawer = () => {
    drawer.value = false;
  };

  const toggleDrawer = () => {
    if (!drawer.value) return openDrawer();
    if (drawerRailMode.value) return (drawerRailMode.value = false);
    return closeDrawer();
  };

  /**
   * Checks if a given drawer item path is currently active.
   * @param {string} itemPath - Target path of the drawer item
   * @returns {boolean} True if the path is active, false otherwise
   */
  const isPathActive = (itemPath: string): boolean => {
    const currentPath = route.path;

    // Direct match
    if (currentPath === itemPath) return true;

    // Match by active path rules
    const extraPaths = navigationActivePathRules.value[itemPath] || [];
    return extraPaths.some((path) => currentPath === path || currentPath.startsWith(`${path}/`));
  };

  return {
    // State
    drawer,
    drawerRailMode,
    openedGroups,

    // Computed
    operationalItems: readonly(operationalItems),
    administrativeItems: readonly(administrativeItems),
    aboutItems: readonly(aboutItems),
    navigationActivePathRules: readonly(navigationActivePathRules),

    // Methods
    toggleDrawer,
    isPathActive,
  } as const;
};
